import { useState, useMemo, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ApolloQueryResult, OperationVariables } from '@apollo/client';

type CompanyRole = {
  companyId: string;
  companyName: string;
  roles: Array<{ id: string; name: string }>;
};

type RoleOption = {
  value: string;
  label: string;
  companyId: string;
  companyName: string;
};

type UseRemoveCompanyRoleProps = {
  selectedUser: {
    id: string;
    name: string;
    email: string;
    companyRoles: CompanyRole[];
  };
  setModalClose: (value: boolean) => void;
  refetch: (
    variables?: Partial<OperationVariables> | undefined
  ) => Promise<ApolloQueryResult<unknown>>;
  // channelId: string;   // will uncomment when backend is ready
};

export const useRemoveCompanyRole = ({
  selectedUser,
  setModalClose,
  refetch,
  // channelId,
}: UseRemoveCompanyRoleProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<RoleOption[]>([]);

  // Reset selected roles when selected user changes to prevent stale selections
  useEffect(() => {
    setSelectedRoles([]);
  }, [selectedUser.id]);

  const onCloseModal = useCallback(() => {
    setModalClose(false);
    setSelectedRoles([]);
  }, [setModalClose]);

  const handleSelectRoles = useCallback((values: RoleOption[]) => {
    setSelectedRoles(values);
  }, []);

  const handleRemoveRoles = useCallback(async () => {
    if (selectedRoles.length === 0) return;

    setLoading(true);

    try {
      // TODO: Replace with actual mutation when backend ready
      // await getClient().mutate({
      //   mutation: removeCompanyRoles,
      //   variables: {
      //     userId: selectedUser.id,
      //     propertyId: channelId,
      //     roleIds: selectedRoles.map(role => role.value),
      //   },
      // });

      await refetch();
      window.Toast.show(
        t('web.admin.channel.users.all.table.removeCompanyRole.success'),
        5000
      );
      onCloseModal();
    } catch (err) {
      await window.Alert.alert({
        title: t('web.admin.channel.users.all.table.removeCompanyRole.error'),
        error: err,
      });
    }

    setLoading(false);
  }, [selectedRoles, refetch, t, onCloseModal]);

  // Map user's current company roles to dropdown options
  const roleOptions: RoleOption[] = useMemo(() => {
    if (!selectedUser.companyRoles?.length) return [];

    return selectedUser.companyRoles.flatMap(companyRole =>
      companyRole.roles.map(role => ({
        value: role.id,
        label: `${role.name} (${companyRole.companyName})`,
        companyId: companyRole.companyId,
        companyName: companyRole.companyName,
      }))
    );
  }, [selectedUser.companyRoles]);

  // Get affected tenants based on selected roles
  const affectedTenants = useMemo(() => {
    const tenantNames = new Set<string>();

    selectedRoles.forEach(role => {
      tenantNames.add(role.companyName);
    });

    return Array.from(tenantNames);
  }, [selectedRoles]);

  const hasNoRoles = roleOptions.length === 0;
  const isButtonDisabled = loading || selectedRoles.length === 0 || hasNoRoles;

  return {
    loading,
    selectedRoles,
    roleOptions,
    affectedTenants,
    hasNoRoles,
    isButtonDisabled,
    onCloseModal,
    handleSelectRoles,
    handleRemoveRoles,
  };
};

